import apiClient from "./api";

export interface Invoice {
  id: number;
  invoiceNumber: string;
  invoiceDate: string;
  clientID: number;
  clientName: string;
  comment: string;
  createdDate: string;
  invoiceTotal: number;
  shippingTotal?: number;
  handlingTotal?: number;
  statusId: number;
  statusName?: string;
}

export interface InvoicePurchaseOrder {
  id: number;
  invoiceId: number;
  purchaseOrderId: number;
  purchaseOrderTotal: number;
  poNumber?: string;
  poDate?: string;
  subClientId?: number;
  subClientName?: string;
  fromInventory?: boolean;
}

export interface InvoiceFull extends Invoice {
  purchaseOrders: InvoicePurchaseOrder[];
}

export interface CreateInvoiceRequest {
  invoiceNumber: string;
  clientID: number;
  clientName: string;
  comment: string;
}

class InvoiceService {
  async getInvoices(offset: number = 0, limit: number = 50, statusID?: number) {
    const params = new URLSearchParams({
      offset: offset.toString(),
      limit: limit.toString(),
    });

    if (statusID !== undefined) {
      params.append("statusID", statusID.toString());
    }

    const response = await apiClient.get(`/invoices?${params.toString()}`);
    return response.data;
  }

  async getInvoiceById(id: number): Promise<InvoiceFull> {
    const response = await apiClient.get(`/invoices/${id}`);
    return response.data;
  }

  async createInvoice(data: CreateInvoiceRequest): Promise<{ id: number }> {
    const response = await apiClient.post("/invoices", data);
    return response.data;
  }

  async updateInvoice(
    id: number,
    data: Partial<CreateInvoiceRequest> & { statusId?: number },
  ): Promise<void> {
    await apiClient.patch(`/invoices/${id}`, data);
  }

  async deleteInvoice(id: number, invoiceNumber: string): Promise<void> {
    await apiClient.delete(`/invoices/${id}`, {
      data: { invoiceNumber },
    });
  }

  async getAvailablePurchaseOrders(clientID: number) {
    const response = await apiClient.get(`/invoices/available-pos/${clientID}`);
    return response.data;
  }

  async addPurchaseOrderToInvoice(
    invoiceId: number,
    purchaseOrderId: number,
    purchaseOrderTotal: number,
  ): Promise<{ id: number }> {
    const response = await apiClient.post(
      `/invoices/${invoiceId}/purchase-orders`,
      {
        purchaseOrderId,
        purchaseOrderTotal,
      },
    );
    return response.data;
  }

  async removePurchaseOrderFromInvoice(
    invoiceId: number,
    linkId: number,
  ): Promise<void> {
    await apiClient.delete(`/invoices/${invoiceId}/purchase-orders/${linkId}`);
  }
}

export const invoiceService = new InvoiceService();
